import heic2any from 'heic2any';
let ffmpegInstance = null;

/**
 * Lazily loads ffmpeg.wasm and returns a singleton FFmpeg instance.
 */
async function getFFmpeg() {
  try{
    if (ffmpegInstance) return ffmpegInstance;

    const ffmpegModule = await import('@ffmpeg/ffmpeg');

    // Some builds (CRA/Vite/Webpack) put everything on default, some on top-level
    // createFFmpeg is usually the default export (a function)
    const createFFmpeg = ffmpegModule?.default || ffmpegModule?.FFmpeg;

    if (typeof createFFmpeg !== 'function') {
        throw new Error('Could not find createFFmpeg function in @ffmpeg/ffmpeg module');
    }

    // Initialize ffmpeg instance
    const ffmpeg = new createFFmpeg({ log: false });

    await ffmpeg.load();

    ffmpegInstance = { ffmpeg };
    }catch(err){
        console.error("Error loading ffmpeg.wasm:", err);   
    }
  return ffmpegInstance;
}

/**
 * Converts a CAF audio Blob to a WAV Blob using ffmpeg.wasm.
 * This function only loads ffmpeg.wasm the first time it’s needed.
 *
 * @param {Blob} cafBlob - The CAF file as a Blob
 * @returns {Promise<Blob>} - Converted WAV file Blob
 */
export async function convertCafToWav(cafBlob) {
    try{
    const { ffmpeg } = await getFFmpeg();

    // Convert Blob to Uint8Array manually
    let uint8Array;
    if (cafBlob instanceof ArrayBuffer) {
        // Already an ArrayBuffer
        uint8Array = new Uint8Array(cafBlob);
    } else if (cafBlob instanceof Blob) {
        // Convert Blob to ArrayBuffer
        const arrayBuffer = await cafBlob.arrayBuffer();
        uint8Array = new Uint8Array(arrayBuffer);
    } else {
        throw new Error('Input must be a Blob or ArrayBuffer');
    }

    // Write CAF to virtual filesystem
    ffmpeg.writeFile('input.caf', uint8Array);

    // Convert CAF → WAV (16-bit PCM)
    await ffmpeg.exec(['-i', 'input.caf', '-acodec', 'pcm_s16le', '-ar', '44100', 'output.wav']);
    // Read and return WAV data
    const data = await ffmpeg.readFile('output.wav');
      return new Blob([data.buffer], { type: 'audio/wav' });
    }catch(err){
        console.error("Error converting CAF to WAV:", err);   
    }
}

/**
 * Convert HEIC image Blob/ArrayBuffer to PNG Blob (or JPG)
 * Works in browsers using @ffmpeg/ffmpeg v0.11+
 */
// export async function convertHeicToPng(heicInput, format = 'png') {
//   const {ffmpeg} = await getFFmpeg();
//   try{
//   let inputData;
//   if (heicInput instanceof ArrayBuffer) {
//     inputData = new Uint8Array(heicInput);
//   } else if (heicInput instanceof Blob) {
//     inputData = new Uint8Array(await heicInput.arrayBuffer());
//   } else {
//     throw new Error('Input must be a Blob or ArrayBuffer');
//   }

//   // Write the HEIC file into the virtual FS
//   ffmpeg.writeFile('input.heic', inputData);

//   // Choose output extension based on requested format
//   const outputExt = format === 'jpg' ? 'jpg' : 'png';
//   const outputName = `output.${outputExt}`;

//   // Convert to chosen format
//   await ffmpeg.exec(['-i', 'input.heic', outputName]);

//   // Read output back from virtual FS
//   const outputData = await ffmpeg.readFile(outputName);

//   return new Blob([outputData], { type: `image/${outputExt}` });
//   }catch(err){
//       console.error("Error converting HEIC to PNG:", err);   
//   }
// }

export async function convertHeicToPng(heicBlob, format = 'image/png') {
  return await heic2any({
    blob: heicBlob,
    toType: format,
  });
}

/**
 * Convert MOV to MP4 in browser if rotation is needed, auto-rotating orientation and providing progress updates
 *
 * @param {Blob|ArrayBuffer} movInput - Input MOV file
 * @param {(progress: number, time: number) => void} onProgress - Callback for progress updates
 * @returns {Promise<Blob>} - Converted MP4 Blob, or original MOV if no rotation needed
 */
export async function convertMovToMp4(movInput, onProgress) {
  const { ffmpeg } = await getFFmpeg();

  // Prepare input data
  let inputData;
  if (movInput instanceof ArrayBuffer) {
    inputData = new Uint8Array(movInput);
  } else if (movInput instanceof Blob) {
    inputData = new Uint8Array(await movInput.arrayBuffer());
  } else {
    throw new Error('Input must be a Blob or ArrayBuffer');
  }

  // Write input MOV to FFmpeg FS
  ffmpeg.writeFile('input.mov', inputData);

  // --- Step 1: Probe rotation metadata ---
  let rotate = 0;
  try {
    await ffmpeg.exec(['-i', 'input.mov'], { log: true });
  } catch (e) {
    // ffmpeg.exec throws when probing; ignore
  }

  // Parse logs to detect rotate metadata
  const logs = ffmpeg.logs || [];
  for (const log of logs) {
    const match = log.message?.match(/rotate\s*:\s*(\d+)/);
    if (match) {
      rotate = parseInt(match[1], 10);
      break;
    }
  }

  // --- Step 2: If no rotation, return original MOV ---
  if (rotate === 0) {
    return movInput instanceof Blob ? movInput : new Blob([inputData], { type: 'video/quicktime' });
  }

  // --- Step 3: Set transpose filter based on rotation ---
  let vfFilter = '';
  if (rotate === 90) vfFilter = 'transpose=1';
  else if (rotate === 180) vfFilter = 'transpose=1,transpose=1';
  else if (rotate === 270) vfFilter = 'transpose=2';

  // --- Step 4: Setup progress listener ---
  if (onProgress) {
    ffmpeg.on('progress', ({ progress, time }) => {
      onProgress(progress, time); // progress: 0..1, time in seconds
    });
  }

  // --- Step 5: Convert to MP4 with rotation applied ---
  const args = [
    '-i', 'input.mov',
    '-metadata:s:v:0', 'rotate=0',
    '-c:v', 'libx264',
    '-c:a', 'aac',
    '-movflags', '+faststart',
    'output.mp4'
  ];

  if (vfFilter) {
    args.splice(3, 0, '-vf', vfFilter); // insert filter after input
  }

  await ffmpeg.exec(args);

  // --- Step 6: Read output MP4 ---
  const outputData = await ffmpeg.readFile('output.mp4');
  return new Blob([outputData], { type: 'video/mp4' });
}
